import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Controller, useForm } from "react-hook-form";
import { Drawer, Divider } from "@mantine/core";
import { HiXMark, HiUser, HiOutlineExclamationCircle } from "react-icons/hi2";
import CustomTextInput from "../../../ui/CustomTextInput/CustomTextInput";
import CustomNumberInput from "../../../ui/CustomNumberInput/CustomNumberInput";
import CustomSelect from "../../../ui/CustomSelect/CustomSelect";
import CustomButton from "../../../ui/CustomButton/CustomButton";
import CustomInlineBanner from "../../../ui/CustomInlineBanner/CustomInlineBanner";
import FetchFeedback from "../../../ui/CustomFetchFeedback/CustomFetchFeedback";
import { useBankOptions } from "../../../../hooks/queries/useBanksQueries";
import { useUpdateBulkRecipient } from "../../../../hooks/queries/useBulkUploadsQueries";
import { extractAxiosErrorMessage } from "../../../../services/utils/errorUtils";
import { toastService } from "../../../../providers";
import { redBannerStyle } from "../fileUploadConfig";
import { getDisplayValue, getInitials } from "../../../../utils/transactionutils";
import {
  recipientValidators as validators,
  REMARKS_MAX_BANK,
  REMARKS_MAX_MPESA,
} from "../../validations/recipientValidators";

function RecipientAvatar({ name }) {
  const initials = getInitials(name);
  return (
    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
      {initials ? (
        <span className="text-sm font-bold text-primary-700">{initials}</span>
      ) : (
        <HiUser size={20} className="text-primary-700" />
      )}
    </div>
  );
}

RecipientAvatar.propTypes = { name: PropTypes.string };

function buildDefaultValues(row) {
  return {
    recipientName: row?.recipientName ?? "",
    phoneNumber: row?.phoneNumber ?? "",
    accountNumber: row?.accountNumber ?? "",
    bankCode: row?.bankCode ?? "",
    amount: row?.amount ?? "",
    remarks: row?.remarks ?? "",
  };
}

function buildPayload(isBank, values) {
  const { recipientName, amount, remarks } = values;
  if (isBank) {
    return {
      recipientName,
      accountNumber: values.accountNumber,
      bankCode: values.bankCode,
      amount,
      remarks,
    };
  }
  return { recipientName, phoneNumber: values.phoneNumber, amount, remarks };
}

export default function RecipientEditDrawer({
  opened,
  recipientRow,
  disbursementId,
  onClose,
  onSuccess,
}) {
  const isBank = recipientRow?.paymentChannel === "Bank";
  const [serverErrors, setServerErrors] = useState([]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm({
    defaultValues: buildDefaultValues(recipientRow),
    mode: "onTouched",
  });

  useEffect(() => {
    reset(buildDefaultValues(recipientRow));
    setServerErrors([]);
  }, [recipientRow, reset]);

  const {
    banks,
    bankOptions,
    bankFilter,
    isBanksLoading,
    isBanksError,
    refetchBanks,
  } = useBankOptions();

  const { mutate: updateRecipient, isPending: isSaving } = useUpdateBulkRecipient();

  const onSubmit = (values) => {
    if (!isDirty) {
      onClose();
      return;
    }
    setServerErrors([]);
    updateRecipient(
      {
        disbursementId,
        recipientId: recipientRow.id,
        payload: buildPayload(isBank, values),
      },
      {
        onSuccess: (data) => {
          if (data.status === "VALIDATION_FAILED") {
            const errors = data.errors ?? ["Validation failed. Please review the details."];
            setServerErrors(errors);
            toastService.error(errors[0]);
            return;
          }
          toastService.success("Recipient updated.");
          onSuccess?.();
        },
        onError: (error) => {
          toastService.error(
            extractAxiosErrorMessage(error, "Failed to update recipient."),
          );
        },
      },
    );
  };

  let bannerMessages = [];
  if (serverErrors.length > 0) {
    bannerMessages = serverErrors;
  } else if (recipientRow?.status === "VALIDATION_FAILED" && recipientRow?.statusDescription) {
    bannerMessages = [recipientRow.statusDescription];
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={{ base: "100%", sm: 400 }}
      withCloseButton={false}
      zIndex={500}
      overlayProps={{ backgroundOpacity: 0.4, blur: 2 }}
      padding="lg"
      classNames={{ body: "!flex !flex-col !h-full" }}
    >
      <div className="shrink-0 flex items-center justify-between mb-1">
        <p className="text-base font-semibold text-gray-900">Edit Recipient</p>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <HiXMark size={18} />
        </button>
      </div>
      <p className="text-xs text-gray-400 mb-5 shrink-0">
        Update Transaction Details for {getDisplayValue(recipientRow?.recipientName)}
      </p>

      <div className="shrink-0 flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <RecipientAvatar name={recipientRow?.recipientName} />
          <p className="text-sm font-medium text-gray-900">
            {getDisplayValue(recipientRow?.recipientName)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-400">Ref ID</p>
          <p className="text-sm font-semibold text-gray-900">
            {getDisplayValue(recipientRow?.batchId)}
          </p>
        </div>
      </div>

      <Divider color="gray.2" mb="lg" className="shrink-0" />

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex-1 min-h-0 flex flex-col"
      >
        <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-4">
          <Controller
            name="recipientName"
            control={control}
            rules={{ validate: (v) => validators.recipientName(v) ?? true }}
            render={({ field, fieldState }) => (
              <CustomTextInput
                label="Recipient Name"
                radius="md"
                withAsterisk
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
              />
            )}
          />

          {isBank ? (
            <>
              <Controller
                name="bankCode"
                control={control}
                rules={{ validate: (v) => validators.bankCode(v) ?? true }}
                render={({ field, fieldState }) => {
                  const selectedBankId =
                    banks.find((b) => b.code === field.value)?.id?.toString() ?? null;
                  const isUnrecognizedCode =
                    !isBanksLoading && !selectedBankId && !!field.value;
                  return (
                    <div className="space-y-1">
                      <CustomSelect
                        label="Bank"
                        placeholder={
                          isBanksLoading ? "Loading banks..." : "Search by name or code"
                        }
                        data={bankOptions}
                        searchable
                        lockOnSelect
                        filter={bankFilter}
                        value={selectedBankId}
                        onChange={(bankId) => {
                          const bank = banks.find((b) => b.id.toString() === bankId);
                          field.onChange(bank?.code ?? "");
                        }}
                        onBlur={field.onBlur}
                        error={fieldState.error?.message}
                        withAsterisk
                        radius="md"
                        disabled={isBanksLoading || isBanksError}
                        comboboxProps={{ withinPortal: false }}
                      />
                      {isUnrecognizedCode && (
                        <p className="text-xs text-gray-400 px-1">
                          Current value on file:{" "}
                          <span className="font-medium text-red-500">
                            {field.value}
                          </span>{" "}
                          — not a recognized bank code, please select a valid bank
                        </p>
                      )}
                    </div>
                  );
                }}
              />

              {isBanksError && (
                <FetchFeedback
                  message="Failed to load banks. Please try again."
                  onRetry={refetchBanks}
                  fullHeight={false}
                  compact
                />
              )}

              <Controller
                name="accountNumber"
                control={control}
                rules={{ validate: (v) => validators.accountNumber(v) ?? true }}
                render={({ field, fieldState }) => (
                  <CustomTextInput
                    label="Account Number"
                    radius="md"
                    withAsterisk
                    value={field.value}
                    onChange={(e) =>
                      field.onChange(e.target.value.replaceAll(/\D/g, "").slice(0, 16))
                    }
                    onBlur={field.onBlur}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </>
          ) : (
            <Controller
              name="phoneNumber"
              control={control}
              rules={{ validate: (v) => validators.phoneNumber(v) ?? true }}
              render={({ field, fieldState }) => (
                <CustomTextInput
                  label="Phone Number"
                  placeholder="254700000000"
                  radius="md"
                  withAsterisk
                  value={field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value.replaceAll(/\D/g, "").slice(0, 12))
                  }
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              )}
            />
          )}

          <Controller
            name="amount"
            control={control}
            rules={{ validate: (v) => validators.amount(v, isBank) ?? true }}
            render={({ field, fieldState }) => (
              <CustomNumberInput
                label="Amount (KES)"
                radius="md"
                withAsterisk
                min={0}
                thousandSeparator=","
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
              />
            )}
          />

          <Controller
            name="remarks"
            control={control}
            rules={{ validate: (v) => validators.remarks(v, isBank) ?? true }}
            render={({ field, fieldState }) => {
              const remarksMaxLength = isBank ? REMARKS_MAX_BANK : REMARKS_MAX_MPESA;
              return (
                <CustomTextInput
                  label="Remarks"
                  radius="md"
                  withAsterisk
                  value={field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value.slice(0, remarksMaxLength))
                  }
                  onBlur={field.onBlur}
                  error={fieldState.error?.message}
                />
              );
            }}
          />

          {bannerMessages.length > 0 && (
            <CustomInlineBanner
              styles={redBannerStyle}
              icon={<HiOutlineExclamationCircle size={14} />}
            >
              <div className="flex flex-col gap-1">
                {bannerMessages.map((message) => (
                  <p key={message} className="text-xs font-medium text-red-600">
                    {message}
                  </p>
                ))}
              </div>
            </CustomInlineBanner>
          )}
        </div>

        <div className="pt-4 shrink-0 flex gap-2">
          <CustomButton
            type="button"
            radius="md"
            variant="default"
            className="flex-1"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </CustomButton>
          <CustomButton
            type="submit"
            radius="md"
            color="primary"
            className="flex-1"
            loading={isSaving}
            disabled={!isDirty}
          >
            Save Changes
          </CustomButton>
        </div>
      </form>
    </Drawer>
  );
}

RecipientEditDrawer.propTypes = {
  opened: PropTypes.bool.isRequired,
  recipientRow: PropTypes.object,
  disbursementId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};
